// src/pages/nfl/NflRangeOfOutcomes.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";

/* ------------------------------------------------------------------
   NFL Range of Outcomes
   - Uses classic projections + point_distributions.json
   - DK / FD / Both toggle
   - Position tabs
   - Salary tag hit rates: 1x, 1.5x, 2x, 2.5x, 3x, 4x
   - Position-aware Good / Boom / Smash / Bust thresholds
   - Range Score for tournament upside with cheap-value guardrails
   - Same dark/light slate theme + logos + clickable proj underline
------------------------------------------------------------------- */

const PROJ_SRC = "/data/nfl/classic/latest/projections.json";
const META_SRC = "/data/nfl/classic/latest/meta.json";

// Your screenshot showed this file here. The hook below also falls back to classic/latest.
const POINT_DIST_SRCS = [
  "/data/nfl/latest/point_distributions.json",
  "/data/nfl/classic/latest/point_distributions.json",
];

const SITES = {
  dk: { key: "dk", label: "DK", logo: "/logos/dk.png" },
  fd: { key: "fd", label: "FD", logo: "/logos/fd.png" },
  both: { key: "both", label: "Both" },
};

const POSITION_TABS = ["ALL", "QB", "RB", "WR", "TE", "DST", "K"];
const MULTS = [1, 1.5, 2, 2.5, 3, 4];

// Position-specific outcome targets.
// Each target uses max(raw fantasy points, salary / 1000 × multiplier).
// That keeps cheap punt/value plays from getting fake boom tags while still
// forcing expensive players to clear salary-adjusted expectations.
const OUTCOME_PROFILES = {
  dk: {
    QB: { goodRaw: 18, boomRaw: 25, smashRaw: 32, bustRaw: 14, goodX: 2.2, boomX: 3.0, smashX: 3.8, bustX: 1.6 },
    RB: { goodRaw: 12, boomRaw: 20, smashRaw: 28, bustRaw: 7, goodX: 2.0, boomX: 3.0, smashX: 4.0, bustX: 1.2 },
    WR: { goodRaw: 12, boomRaw: 20, smashRaw: 30, bustRaw: 7, goodX: 2.0, boomX: 3.0, smashX: 4.0, bustX: 1.2 },
    TE: { goodRaw: 9, boomRaw: 15, smashRaw: 23, bustRaw: 5, goodX: 1.8, boomX: 2.8, smashX: 3.8, bustX: 1.0 },
    K: { goodRaw: 8, boomRaw: 12, smashRaw: 16, bustRaw: 5, goodX: 1.8, boomX: 2.6, smashX: 3.3, bustX: 1.1 },
    DST: { goodRaw: 7, boomRaw: 10, smashRaw: 16, bustRaw: 3, goodX: 1.8, boomX: 2.6, smashX: 3.8, bustX: 0.8 },
    DEFAULT: { goodRaw: 12, boomRaw: 20, smashRaw: 28, bustRaw: 7, goodX: 2.0, boomX: 3.0, smashX: 4.0, bustX: 1.2 },
  },
  fd: {
    QB: { goodRaw: 17, boomRaw: 24, smashRaw: 31, bustRaw: 13, goodX: 2.0, boomX: 2.8, smashX: 3.6, bustX: 1.5 },
    RB: { goodRaw: 11, boomRaw: 18, smashRaw: 26, bustRaw: 6, goodX: 1.8, boomX: 2.7, smashX: 3.6, bustX: 1.0 },
    WR: { goodRaw: 10, boomRaw: 18, smashRaw: 27, bustRaw: 6, goodX: 1.8, boomX: 2.7, smashX: 3.6, bustX: 1.0 },
    TE: { goodRaw: 8, boomRaw: 13, smashRaw: 20, bustRaw: 4, goodX: 1.6, boomX: 2.5, smashX: 3.4, bustX: 0.9 },
    K: { goodRaw: 8, boomRaw: 12, smashRaw: 16, bustRaw: 5, goodX: 1.8, boomX: 2.6, smashX: 3.3, bustX: 1.1 },
    DST: { goodRaw: 7, boomRaw: 10, smashRaw: 16, bustRaw: 3, goodX: 1.8, boomX: 2.6, smashX: 3.8, bustX: 0.8 },
    DEFAULT: { goodRaw: 11, boomRaw: 18, smashRaw: 26, bustRaw: 6, goodX: 1.8, boomX: 2.7, smashX: 3.6, bustX: 1.0 },
  },
};

/* ------------------------------ helpers ------------------------------- */
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const fmt0 = (v) => {
  const n = num(v);
  return n == null ? "" : n.toLocaleString();
};

const fmt1 = (v) => {
  const n = num(v);
  return n == null ? "" : n.toFixed(1);
};

const fmtPct = (v, decimals = 1) => {
  const n = num(v);
  return n == null ? "" : `${n.toFixed(decimals)}%`;
};

const fmtScore = (v) => {
  const n = num(v);
  return n == null ? "" : n.toFixed(1);
};

const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");

const cleanPos = (v) => {
  const p = String(v || "").toUpperCase().trim();
  if (["D", "DEF", "DEFENSE", "D/ST"].includes(p)) return "DST";
  if (p.includes("/")) return p.split("/")[0];
  return p || "";
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    if (obj && k in obj && obj[k] !== "" && obj[k] != null) return obj[k];
  }
  return fallback;
};

const teamLogo = (team) => `/logos/nfl/${String(team || "").toUpperCase()}.png`;
const teamLogoAlt = (team) => `/logos/${String(team || "").toUpperCase()}.png`;

const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};

function downloadCSV(rows, cols, fname = "nfl_range_of_outcomes.csv") {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          let val = r[c.key];
          if (c.type === "money") val = fmt0(val);
          if (c.type === "num1") val = fmt1(val);
          if (c.type === "pct") val = fmtPct(val);
          if (c.type === "score") val = fmtScore(val);
          return escapeCSV(val ?? "");
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/* ------------------------------ fetchers ------------------------------- */
function useJson(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);

  return { data, loading, err };
}

function useJsonFallback(urls) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [usedUrl, setUsedUrl] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      setUsedUrl("");
      for (const url of urls) {
        try {
          const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const j = await r.json();
          if (alive) {
            setData(j);
            setUsedUrl(url);
            setLoading(false);
          }
          return;
        } catch (e) {
          if (url === urls[urls.length - 1] && alive) {
            setErr(String(e));
            setLoading(false);
          }
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [urls.join("|")]);

  return { data, loading, err, usedUrl };
}

function useMeta(url) {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setMeta(j);
      } catch {
        if (alive) setMeta(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);

  return meta;
}

const fmtUpdated = (meta) => {
  const raw =
    meta?.last_updated ||
    meta?.updated_iso ||
    meta?.updated ||
    meta?.generated_at ||
    meta?.artifacts?.find?.((x) => String(x.path || "").endsWith("projections.json"))
      ?.last_updated ||
    null;

  if (!raw && !meta?.updated_epoch) return "—";

  const d = raw ? new Date(raw) : new Date(meta.updated_epoch * 1000);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/* ---------------------- distribution math ---------------------- */
function getBinsFromEntry(entry) {
  if (Array.isArray(entry)) return entry;
  if (Array.isArray(entry?.bins)) return entry.bins;
  return [];
}

function probAbove(bins, target) {
  const t = num(target);
  if (t == null || !Array.isArray(bins)) return null;

  let p = 0;
  for (const b of bins) {
    const a = Number(b?.bin_start);
    const z = Number(b?.bin_end);
    const bp = Number(b?.prob);
    if (!Number.isFinite(a) || !Number.isFinite(z) || !Number.isFinite(bp)) continue;
    if (z <= t) continue;
    if (a >= t) {
      p += bp;
      continue;
    }
    // Uniform within-bin partial credit when threshold splits the bin.
    const width = z - a;
    if (width > 0) p += bp * ((z - t) / width);
  }
  return Math.max(0, Math.min(1, p)) * 100;
}

function probBelowOrEqual(bins, target) {
  const t = num(target);
  if (t == null || !Array.isArray(bins)) return null;

  let p = 0;
  for (const b of bins) {
    const a = Number(b?.bin_start);
    const z = Number(b?.bin_end);
    const bp = Number(b?.prob);
    if (!Number.isFinite(a) || !Number.isFinite(z) || !Number.isFinite(bp)) continue;
    if (a >= t) continue;
    if (z <= t) {
      p += bp;
      continue;
    }
    const width = z - a;
    if (width > 0) p += bp * ((t - a) / width);
  }
  return Math.max(0, Math.min(1, p)) * 100;
}

function meanFromBins(bins) {
  if (!Array.isArray(bins) || !bins.length) return null;
  let totalP = 0;
  let mean = 0;
  for (const b of bins) {
    const a = Number(b?.bin_start);
    const z = Number(b?.bin_end);
    const p = Number(b?.prob);
    if (!Number.isFinite(a) || !Number.isFinite(z) || !Number.isFinite(p)) continue;
    mean += ((a + z) / 2) * p;
    totalP += p;
  }
  return totalP > 0 ? mean / totalP : null;
}

function buildPointDistIndex(raw) {
  const pd = raw && typeof raw === "object" ? raw : null;
  if (!pd) return { dk: {}, fd: {} };

  const make = (obj) => {
    const out = {};
    if (!obj || typeof obj !== "object") return out;

    const add = (key, bins) => {
      const k = keynorm(key);
      if (k && Array.isArray(bins) && bins.length && !out[k]) out[k] = bins;
    };

    for (const [rawKey, entry] of Object.entries(obj)) {
      const bins = getBinsFromEntry(entry);
      const parts = String(rawKey).split("|").map((x) => x.trim());

      const team = String(entry?.team || (parts.length >= 2 ? parts[0] : "")).toUpperCase();
      const player = entry?.player || (parts.length >= 2 ? parts[1] : rawKey);
      const slot = entry?.slot || (parts.length >= 3 ? parts[2] : "");
      const pos = cleanPos(slot);

      add(rawKey, bins);
      add(player, bins);
      add(`${team} ${player}`, bins);
      add(`${team}|${player}|${slot}`, bins);
      add(`${team}|${player}|${pos}`, bins);
      add(`${player}|${pos}`, bins);
    }

    return out;
  };

  return {
    dk: make(pd.dk ?? pd.DK ?? {}),
    fd: make(pd.fd ?? pd.FD ?? {}),
  };
}

function findBins(index, siteKey, row) {
  const siteIndex = index?.[siteKey] || {};
  const player = row?.player || "";
  const team = row?.team || "";
  const pos = cleanPos(row?.pos);

  const candidates = [
    `${team}|${player}|${pos}`,
    `${team} ${player}`,
    `${player}|${pos}`,
    player,
    row?.rawPlayer,
    `${team}|${row?.rawPlayer}|${pos}`,
    `${team} ${row?.rawPlayer}`,
  ].filter(Boolean);

  for (const c of candidates) {
    const hit = siteIndex[keynorm(c)];
    if (hit) return hit;
  }
  return [];
}

function getProfile(siteKey, pos) {
  const site = siteKey === "fd" ? "fd" : "dk";
  const clean = cleanPos(pos);
  return OUTCOME_PROFILES[site][clean] || OUTCOME_PROFILES[site].DEFAULT;
}

function targetFromProfile(salary, rawTarget, multTarget) {
  const s = num(salary);
  if (s == null || s <= 0) return null;
  return Math.max(rawTarget, (s / 1000) * multTarget);
}

function getOutcomeTargets(siteKey, pos, salary) {
  const p = getProfile(siteKey, pos);
  return {
    goodTarget: targetFromProfile(salary, p.goodRaw, p.goodX),
    boomTarget: targetFromProfile(salary, p.boomRaw, p.boomX),
    smashTarget: targetFromProfile(salary, p.smashRaw, p.smashX),
    bustTarget: targetFromProfile(salary, p.bustRaw, p.bustX),
    goodRaw: p.goodRaw,
    boomRaw: p.boomRaw,
    smashRaw: p.smashRaw,
    bustRaw: p.bustRaw,
    goodX: p.goodX,
    boomX: p.boomX,
    smashX: p.smashX,
    bustX: p.bustX,
  };
}

function rangeScore(row) {
  const good = num(row.good) ?? 0;
  const boom = num(row.boom) ?? 0;
  const smash = num(row.smash) ?? 0;
  const bust = num(row.bust) ?? 0;
  const p2x = num(row.p2x) ?? 0;
  const p25x = num(row.p25x) ?? 0;
  const p3x = num(row.p3x) ?? 0;
  const p4x = num(row.p4x) ?? 0;

  const pos = cleanPos(row.pos);
  const isQB = pos === "QB";
  const isTE = pos === "TE";
  const isDST = pos === "DST";
  const isK = pos === "K";

  const goodW = isQB ? 0.42 : isK || isDST ? 0.22 : 0.28;
  const boomW = isQB ? 0.70 : isTE ? 0.62 : 0.66;
  const smashW = isQB ? 0.62 : isK || isDST ? 0.58 : 0.82;
  const bustW = isQB ? 0.40 : isK || isDST ? 0.24 : 0.34;

  // Keep some salary-value signal, but make it secondary to true point outcomes.
  const valueSignal = p4x * 0.28 + p3x * 0.18 + p25x * 0.10 + p2x * 0.05;

  const score = good * goodW + boom * boomW + smash * smashW + valueSignal - bust * bustW;
  return Math.max(0, score);
}

/* -------------------------- heat text -------------------------- */
function dirForKey(k) {
  const id = String(k).toLowerCase();
  if (["range", "good", "boom", "smash", "p1x", "p15x", "p2x", "p25x", "p3x", "p4x", "proj", "val"].includes(id)) {
    return "higher";
  }
  if (id === "bust") return "lower";
  return null;
}

function heatTextColor(min, max, v, dir, theme = "dark") {
  if (v == null || min == null || max == null || min === max || !dir) return null;

  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;

  const isDark = theme === "dark";
  let hue;
  let sat;
  let light;

  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 80;
      light = 62 + u * 10;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 75;
      light = 72 - u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      hue = 0 + u * 60;
      sat = 85;
      light = 42 + u * 6;
    } else {
      const u = (t - 0.5) / 0.5;
      hue = 60 + u * 60;
      sat = 80;
      light = 38 - u * 4;
    }
  }

  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

/* ----------------------- distribution modal ----------------------- */
function PointDistModal({ open, onClose, isDark, site, playerName, bins, proj }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const chartData = Array.isArray(bins)
    ? bins.map((b, idx) => {
        const a = Number(b?.bin_start);
        const z = Number(b?.bin_end);
        const p = Number(b?.prob);
        const label = Number.isFinite(a) && Number.isFinite(z) ? `${a}-${z}` : `bin-${idx + 1}`;
        return {
          label,
          shortLabel: Number.isFinite(a) ? `${a}` : label,
          bin_start: a,
          bin_end: z,
          mid: Number.isFinite(a) && Number.isFinite(z) ? (a + z) / 2 : idx,
          probPct: Number.isFinite(p) ? p * 100 : 0,
        };
      })
    : [];

  const hasData = chartData.length > 0;

  const projX = Number.isFinite(proj) ? proj : null;

  const xDomain = useMemo(() => {
    const mids = chartData
      .map((d) => d.mid)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    if (!mids.length) return [0, 1];
    if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];

    const firstGap = Math.abs(mids[1] - mids[0]) || 1;
    const lastGap =
      Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || firstGap;

    return [
      mids[0] - firstGap / 2,
      mids[mids.length - 1] + lastGap / 2,
    ];
  }, [chartData]);

  const xTicks = useMemo(() => {
    if (!chartData.length) return [];

    return chartData
      .filter((_, i) => (isMobile ? i % 4 === 0 : i % 2 === 0))
      .map((d) => d.mid)
      .filter(Number.isFinite);
  }, [chartData, isMobile]);

  // Hooks must run in the same order on every render.
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className={[
          "relative w-full max-w-3xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-y-auto",
          isDark
            ? "border-slate-700/60 bg-slate-950 text-slate-100"
            : "border-slate-200 bg-white text-slate-900",
        ].join(" ")}
      >
        <div className={isDark ? "bg-slate-950/95" : "bg-white"}>
          <div className="flex items-start justify-between gap-3 px-3 pt-4 sm:gap-4 sm:px-5 sm:pt-5">
            <div className="min-w-0">
              <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                Fantasy Points Distribution
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="max-w-[190px] truncate text-base font-extrabold tracking-tight sm:max-w-none sm:text-lg">
                  {playerName || "—"}
                </div>

                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide",
                    site === "DK"
                      ? isDark
                        ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                        : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : isDark
                      ? "bg-sky-500/15 text-sky-200 border border-sky-500/30"
                      : "bg-sky-50 text-sky-800 border border-sky-200",
                  ].join(" ")}
                >
                  {site}
                </span>

                {Number.isFinite(proj) ? (
                  <span className={isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    Proj: <span className="font-semibold tabular-nums">{proj.toFixed(1)}</span>
                  </span>
                ) : null}
              </div>
            </div>

            <button
              className={[
                "shrink-0 rounded-xl border px-2.5 py-1.5 text-xs font-semibold sm:px-3 sm:py-2 sm:text-sm",
                isDark
                  ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100",
              ].join(" ")}
              onClick={onClose}
            >
              Close
            </button>
          </div>

          <div className="px-2 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
            {!hasData ? (
              <div className={isDark ? "text-sm text-slate-300" : "text-sm text-slate-700"}>
                No distribution data found for this player yet.
              </div>
            ) : (
              <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 18,
                      left: 8,
                      bottom: isMobile ? 42 : 18,
                    }}
                    barCategoryGap={isMobile ? "12%" : "8%"}
                  >
                    <CartesianGrid stroke={isDark ? "#334155" : "#e5e7eb"} strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="mid"
                      domain={xDomain}
                      ticks={xTicks}
                      tickFormatter={(v) => {
                        const hit = chartData.find((d) => d.mid === v);
                        return hit?.shortLabel ?? `${Math.round(v)}`;
                      }}
                      tick={{
                        fontSize: isMobile ? 11 : 12,
                        fill: isDark ? "#cbd5f5" : "#334155",
                      }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                      angle={isMobile ? -35 : 0}
                      textAnchor={isMobile ? "end" : "middle"}
                      height={isMobile ? 52 : 30}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: isMobile ? 10 : 11, fill: isDark ? "#cbd5f5" : "#334155" }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                      width={isMobile ? 38 : 60}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.12)" }}
                      contentStyle={{
                        backgroundColor: isDark ? "#020617" : "#ffffff",
                        border: "1px solid #38bdf8",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: isDark ? "#e2e8f0" : "#0f172a" }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]}
                      labelFormatter={(l) => `Bin ${l}`}
                    />
                    {Number.isFinite(projX) ? (
                      <ReferenceLine x={projX} stroke="#38bdf8" strokeWidth={2} strokeDasharray="6 4" />
                    ) : null}
                    <Bar
                      dataKey="probPct"
                      radius={[6, 6, 0, 0]}
                      fill="#7dd3fc"
                      stroke="#38bdf8"
                      strokeWidth={1}
                      maxBarSize={42}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- page -------------------------------- */
export default function NflRangeOfOutcomes() {
  const { data: projRaw, loading: projLoading, err: projErr } = useJson(PROJ_SRC);
  const meta = useMeta(META_SRC);
  const { data: pointDistRaw, loading: pdLoading, err: pdErr } = useJsonFallback(POINT_DIST_SRCS);

  const [site, setSite] = useState("dk"); // dk | fd | both
  const [posTab, setPosTab] = useState("ALL");
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("dark");
  const [sort, setSort] = useState({ key: "range", dir: "desc" });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalSite, setModalSite] = useState("DK");
  const [modalPlayer, setModalPlayer] = useState("");
  const [modalBins, setModalBins] = useState([]);
  const [modalProj, setModalProj] = useState(null);

  const isDark = theme === "dark";

  const rawRows = useMemo(() => {
    const arr = Array.isArray(projRaw) ? projRaw : projRaw?.rows ?? projRaw?.data ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [projRaw]);

  const pointIndex = useMemo(() => buildPointDistIndex(pointDistRaw), [pointDistRaw]);

  const baseRows = useMemo(() => {
    return rawRows.map((r, idx) => {
      const rawPlayer = pick(r, ["player", "Player", "name", "Name"], "");
      const pos = cleanPos(pick(r, ["pos", "Pos", "position", "Position"], ""));
      const team = String(pick(r, ["team", "Team"], "")).toUpperCase();
      const opp = String(pick(r, ["opp", "Opp", "OPP", "opponent"], "")).toUpperCase();

      const dkSal = pick(r, ["dk_sal", "DK Sal", "dk_salary", "DK Salary"]);
      const fdSal = pick(r, ["fd_sal", "FD Sal", "fd_salary", "FD Salary"]);
      const dkProj = pick(r, ["dk_proj", "DK Proj", "dk_projection", "DK Projection"]);
      const fdProj = pick(r, ["fd_proj", "FD Proj", "fd_projection", "FD Projection"]);
      const dkPown = pick(r, ["dk_pown", "DK pOWN%", "DK pOWN", "dk_own"]);
      const fdPown = pick(r, ["fd_pown", "FD pOWN%", "FD pOWN", "fd_own"]);

      return {
        id: `${team}-${rawPlayer}-${pos}-${idx}`,
        rawPlayer,
        player: rawPlayer,
        pos,
        team,
        opp,
        dkSal,
        fdSal,
        dkProj,
        fdProj,
        dkPown,
        fdPown,
      };
    });
  }, [rawRows]);

  const outcomeRows = useMemo(() => {
    const rows = [];

    for (const r of baseRows) {
      const siteKeys = site === "both" ? ["dk", "fd"] : [site];

      for (const sk of siteKeys) {
        const sal = sk === "dk" ? r.dkSal : r.fdSal;
        const projRawVal = sk === "dk" ? r.dkProj : r.fdProj;
        const pown = sk === "dk" ? r.dkPown : r.fdPown;
        const salary = num(sal);
        const proj = num(projRawVal);
        if (salary == null || salary <= 0) continue;

        const bins = findBins(pointIndex, sk, r);
        const binMean = meanFromBins(bins);
        const effectiveProj = proj ?? binMean;

        const tag = salary / 1000;
        const thresholds = Object.fromEntries(MULTS.map((m) => [`tag${String(m).replace(".", "")}`, tag * m]));
        const p1x = probAbove(bins, tag * 1);
        const p15x = probAbove(bins, tag * 1.5);
        const p2x = probAbove(bins, tag * 2);
        const p25x = probAbove(bins, tag * 2.5);
        const p3x = probAbove(bins, tag * 3);
        const p4x = probAbove(bins, tag * 4);

        const targets = getOutcomeTargets(sk, r.pos, salary);
        const good = probAbove(bins, targets.goodTarget);
        const boom = probAbove(bins, targets.boomTarget);
        const smash = probAbove(bins, targets.smashTarget);
        const bust = probBelowOrEqual(bins, targets.bustTarget);
        const val = effectiveProj == null ? null : effectiveProj / tag;

        const rowOut = {
          ...r,
          site: sk.toUpperCase(),
          sal: salary,
          proj: effectiveProj,
          pown,
          tag,
          threshold1x: thresholds.tag1,
          threshold15x: thresholds.tag15,
          threshold2x: thresholds.tag2,
          threshold25x: thresholds.tag25,
          threshold3x: thresholds.tag3,
          threshold4x: thresholds.tag4,
          p1x,
          p15x,
          p2x,
          p25x,
          p3x,
          p4x,
          good,
          boom,
          smash,
          bust,
          goodTarget: targets.goodTarget,
          boomTarget: targets.boomTarget,
          smashTarget: targets.smashTarget,
          bustTarget: targets.bustTarget,
          goodRaw: targets.goodRaw,
          boomRaw: targets.boomRaw,
          smashRaw: targets.smashRaw,
          bustRaw: targets.bustRaw,
          goodX: targets.goodX,
          boomX: targets.boomX,
          smashX: targets.smashX,
          bustX: targets.bustX,
          val,
          bins,
          hasDist: Array.isArray(bins) && bins.length > 0,
        };

        rowOut.range = rangeScore(rowOut);
        rows.push(rowOut);
      }
    }

    return rows;
  }, [baseRows, pointIndex, site]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return outcomeRows.filter((r) => {
      const posOk = posTab === "ALL" || cleanPos(r.pos) === posTab;
      if (!posOk) return false;
      if (!s) return true;
      return `${r.player} ${r.pos} ${r.team} ${r.opp} ${r.site}`.toLowerCase().includes(s);
    });
  }, [outcomeRows, posTab, q]);

  const columns = useMemo(() => {
    const showSite = site === "both";
    return [
      { key: "player", label: "Player", type: "text", thClass: "w-[185px]", tdClass: "w-[185px] max-w-[185px]" },
      ...(showSite ? [{ key: "site", label: "Site", type: "text", thClass: "w-[54px]", tdClass: "w-[54px]" }] : []),
      { key: "pos", label: "Pos", type: "text", thClass: "w-[52px]", tdClass: "w-[52px]" },
      { key: "team", label: "Team", type: "text", thClass: "w-[60px]", tdClass: "w-[60px]" },
      { key: "opp", label: "Opp", type: "text", thClass: "w-[60px]", tdClass: "w-[60px]" },
      { key: "sal", label: "Sal", type: "money", thClass: "w-[82px]", tdClass: "w-[82px]" },
      { key: "proj", label: "Proj", type: "num1", thClass: "w-[70px]", tdClass: "w-[70px]" },
      { key: "val", label: "X Val", type: "num1", thClass: "w-[68px]", tdClass: "w-[68px]" },
      { key: "p1x", label: "1x%", type: "pct", thClass: "w-[70px]", tdClass: "w-[70px]" },
      { key: "p15x", label: "1.5x%", type: "pct", thClass: "w-[76px]", tdClass: "w-[76px]" },
      { key: "p2x", label: "2x%", type: "pct", thClass: "w-[70px]", tdClass: "w-[70px]" },
      { key: "p25x", label: "2.5x%", type: "pct", thClass: "w-[76px]", tdClass: "w-[76px]" },
      { key: "p3x", label: "3x%", type: "pct", thClass: "w-[70px]", tdClass: "w-[70px]" },
      { key: "p4x", label: "4x%", type: "pct", thClass: "w-[70px]", tdClass: "w-[70px]" },
      { key: "good", label: "Good%", type: "pct", thClass: "w-[76px]", tdClass: "w-[76px]" },
      { key: "boom", label: "Boom%", type: "pct", thClass: "w-[78px]", tdClass: "w-[78px]" },
      { key: "smash", label: "Smash%", type: "pct", thClass: "w-[82px]", tdClass: "w-[82px]" },
      { key: "bust", label: "Bust%", type: "pct", thClass: "w-[76px]", tdClass: "w-[76px]" },
      { key: "range", label: "Range", type: "score", thClass: "w-[78px]", tdClass: "w-[78px]" },
    ];
  }, [site]);

  useEffect(() => {
    setSort({ key: "range", dir: "desc" });
  }, [site, posTab]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const arr = [...filtered];
    const typeFor = (k) => columns.find((c) => c.key === k)?.type;

    arr.sort((a, b) => {
      const t = typeFor(sort.key);
      const av = t === "text" ? String(a[sort.key] ?? "").toLowerCase() : num(a[sort.key]);
      const bv = t === "text" ? String(b[sort.key] ?? "").toLowerCase() : num(b[sort.key]);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (t === "text") return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
      return (av - bv) * dir;
    });
    return arr;
  }, [filtered, sort, columns]);

  const heatStats = useMemo(() => {
    const stats = {};
    for (const c of columns) {
      const dir = dirForKey(c.key);
      if (!dir) continue;
      let min = Infinity;
      let max = -Infinity;
      for (const r of sorted) {
        const v = num(r[c.key]);
        if (v == null) continue;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (min !== Infinity && max !== -Infinity) stats[c.key] = { min, max, dir };
    }
    return stats;
  }, [sorted, columns]);

  const onSort = (col) => {
    setSort((prev) => {
      if (prev.key !== col.key) return { key: col.key, dir: "desc" };
      return { key: col.key, dir: prev.dir === "desc" ? "asc" : "desc" };
    });
  };

  const openDist = (row) => {
    setModalSite(row.site);
    setModalPlayer(row.player);
    setModalBins(row.bins || []);
    setModalProj(num(row.proj));
    setModalOpen(true);
  };

  const loading = projLoading || pdLoading;
  const err = projErr || pdErr;
  const withDists = outcomeRows.filter((r) => r.hasDist).length;

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  const headerBgClass = isDark ? "bg-slate-900 text-slate-200" : "bg-slate-50 text-slate-700";
  const headerBgCell = isDark ? "bg-slate-900" : "bg-slate-50";
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";
  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";
  const textSz = "text-[11px] md:text-[12px]";
  const cell = "px-2 py-1 text-center";
  const headerBase = "px-2 py-1 font-semibold text-center";

  return (
    <div className={outerClass}>
      <PointDistModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        isDark={isDark}
        site={modalSite}
        playerName={modalPlayer}
        bins={modalBins}
        proj={modalProj}
      />

      <div className="w-full px-4 md:px-6 py-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">NFL — Range of Outcomes</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {fmtUpdated(meta)}</span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </div>

        <section aria-label="Site" className={cardClass + " mb-4 px-4 py-3"}>
          <div className={"mb-1 text-[11px] font-semibold " + (isDark ? "text-slate-400" : "text-slate-500")}>Site</div>
            <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {["dk", "fd", "both"].map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setSite(k);
                    setModalOpen(false);
                  }}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === k ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                  }`}
                >
                  {SITES[k].logo ? <img src={SITES[k].logo} alt={SITES[k].label} className="w-4 h-4" /> : null}
                  <span>{SITES[k].label}</span>
                </button>
              ))}
            </div>

        </section>
        <section aria-label="Range of outcomes" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <div>
              <h2 className="text-sm font-semibold">Player Outcomes</h2>
              <p className={"mt-1 text-[11px] " + (isDark ? "text-slate-400" : "text-slate-500")}>Click an underlined projection for its distribution chart.</p>
            </div>
            <button
              className="h-8 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400"
              onClick={() => downloadCSV(sorted, columns)}
            >
              Export CSV
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {POSITION_TABS.map((p) => (
                <button
                  key={p}
                  onClick={() => {
                    setPosTab(p);
                    setModalOpen(false);
                  }}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    posTab === p ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : (isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")
                  }`}
                >
                  {p === "ALL" ? "All" : p}
                </button>
              ))}
            </div>

            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search player / team / opp…"
              className={
                "h-8 w-full sm:w-72 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
              }
            />

          </div>


          <div
            className={
              "px-4 py-3 border-b text-xs leading-relaxed " +
              (isDark ? "border-slate-800/80 text-slate-400" : "border-slate-200 text-slate-600")
            }
          >
            Salary-tag columns still use <span className="font-semibold">salary / 1000 × multiplier</span>. Good/Boom/Smash/Bust use
            position-aware raw-point floors plus salary-adjusted targets. That keeps cheap players from getting fake boom tags while
            still forcing expensive players to clear their salary-based ceiling expectations.
          </div>

          <div className="overflow-auto max-h-[72vh] rounded-b-2xl">
            <table className={`border-separate table-auto w-max min-w-full ${textSz} ${tableTextClass}`} style={{ borderSpacing: 0 }}>
              <thead className={`${headerBgClass} sticky top-0 z-20 shadow-[0_2px_0_0_rgba(0,0,0,0.35)]`}>
                <tr>
                  {columns.map((c, idx) => (
                    <th
                      key={c.key}
                      className={`${headerBase} whitespace-nowrap cursor-pointer select-none ${c.thClass || ""} ${headerBgCell} ${
                        idx === 0 ? "sticky left-0 z-30" : ""
                      }`}
                      onClick={() => onSort(c)}
                      title="Click to sort"
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{c.label}</span>
                        {sort.key === c.key ? (
                          <span className="text-[10px] text-slate-400">{sort.dir === "desc" ? "▼" : "▲"}</span>
                        ) : (
                          <span className="text-[9px] text-slate-600">▲</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td className={`${cell} text-slate-400`} colSpan={columns.length}>
                      Loading…
                    </td>
                  </tr>
                )}

                {err && !loading && (
                  <tr>
                    <td className={`${cell} text-red-400`} colSpan={columns.length}>
                      Failed to load: {err}
                    </td>
                  </tr>
                )}

                {!loading &&
                  !err &&
                  sorted.map((r, i) => {
                    const rowBg = i % 2 === 0 ? rowOddClass : rowEvenClass;

                    return (
                      <tr key={`${r.id}-${r.site}`} className={rowBg}>
                        {columns.map((c, idx) => {
                          if (c.key === "player") {
                            return (
                              <td
                                key={c.key}
                                className={`px-2 py-1 text-left sticky left-0 z-10 ${rowBg} shadow-[inset_-6px_0_8px_-6px_rgba(0,0,0,0.6)] w-[185px] max-w-[185px]`}
                                title={r.player}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <img
                                    src={teamLogo(r.team)}
                                    alt={r.team}
                                    className="w-4 h-4 rounded-sm object-contain flex-none"
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = teamLogoAlt(r.team);
                                    }}
                                  />
                                  <span className="truncate">{r.player}</span>
                                </div>
                              </td>
                            );
                          }

                          const stat = heatStats[c.key];
                          const vNum = num(r[c.key]);
                          const textColor = stat && vNum != null ? heatTextColor(stat.min, stat.max, vNum, stat.dir, theme) : null;

                          let content = r[c.key];
                          if (c.type === "money") content = fmt0(content);
                          if (c.type === "num1") content = fmt1(content);
                          if (c.type === "pct") content = fmtPct(content);
                          if (c.type === "score") content = fmtScore(content);

                          const isProj = c.key === "proj";
                          const missingDist = isProj && !r.hasDist;

                          return (
                            <td
                              key={c.key}
                              className={`${c.tdClass || ""} text-center py-1 whitespace-nowrap ${
                                c.type !== "text" ? "tabular-nums px-1.5" : "px-2"
                              }`}
                              style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                              title={
                                c.key === "good"
                                  ? `Chance to score ${fmt1(r.goodTarget)}+ points. Target = max(${r.goodRaw} raw, ${r.goodX}x salary)`
                                  : c.key === "boom"
                                  ? `Chance to score ${fmt1(r.boomTarget)}+ points. Target = max(${r.boomRaw} raw, ${r.boomX}x salary)`
                                  : c.key === "smash"
                                  ? `Chance to score ${fmt1(r.smashTarget)}+ points. Target = max(${r.smashRaw} raw, ${r.smashX}x salary)`
                                  : c.key === "bust"
                                  ? `Chance to score ${fmt1(r.bustTarget)} or fewer points. Target = max(${r.bustRaw} raw, ${r.bustX}x salary)`
                                  : isProj
                                  ? r.hasDist
                                    ? "Click for distribution"
                                    : "No distribution found"
                                  : String(content ?? "")
                              }
                            >
                              {isProj ? (
                                <button
                                  type="button"
                                  disabled={!r.hasDist}
                                  onClick={() => openDist(r)}
                                  className={[
                                    "inline-flex items-center justify-center rounded-md px-1.5 py-0.5 font-semibold transition",
                                    r.hasDist
                                      ? isDark
                                        ? "text-sky-200 hover:bg-sky-500/15"
                                        : "text-sky-700 hover:bg-sky-50"
                                      : "opacity-60 cursor-not-allowed",
                                  ].join(" ")}
                                  style={
                                    r.hasDist
                                      ? {
                                          textDecorationLine: "underline",
                                          textUnderlineOffset: "4px",
                                          textDecorationThickness: "1px",
                                          borderBottom: isDark
                                            ? "1px solid rgba(125, 211, 252, 0.45)"
                                            : "1px solid rgba(2, 132, 199, 0.45)",
                                        }
                                      : undefined
                                  }
                                >
                                  {content}
                                </button>
                              ) : (
                                content
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}

                {!loading && !err && !sorted.length && (
                  <tr>
                    <td className={`${cell} text-slate-400`} colSpan={columns.length}>
                      No data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
